import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const SPA_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const spaClient = axios.create({
  baseURL: SPA_API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

spaClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

spaClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

export type SpaTreatmentStatus = 'active' | 'inactive' | 'maintenance';
export type SpaRoomStatus = 'available' | 'occupied' | 'cleaning' | 'maintenance';
export type SpaAppointmentStatus =
  | 'pending'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show';
export type TherapistSpecialty =
  | 'massage'
  | 'facial'
  | 'body'
  | ' Reflexology'
  | 'aromatherapy'
  | 'hydrotherapy';

export interface SpaTreatment {
  id: string;
  name: string;
  description?: string;
  code: string;
  category: string;
  duration_minutes: number;
  price: number;
  currency: string;
  image_url?: string;
  status: SpaTreatmentStatus;
  is_featured: boolean;
  sort_order: number;
  amenities?: string[];
  contraindications?: string[];
  created_at: string;
  updated_at: string;
}

export interface SpaRoom {
  id: string;
  name: string;
  code: string;
  room_type: string;
  capacity: number;
  area_sqm?: number;
  status: SpaRoomStatus;
  floor_level?: number;
  amenities?: string[];
  image_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Therapist {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone: string;
  specialties: TherapistSpecialty[];
  certification?: string;
  years_experience?: number;
  bio?: string;
  image_url?: string;
  is_active: boolean;
  working_days?: string[];
  working_hours_start?: string;
  working_hours_end?: string;
  created_at: string;
  updated_at: string;
}

export interface SpaAppointment {
  id: string;
  appointment_reference: string;
  guest_id?: string;
  booking_id?: string;
  guest_name: string;
  guest_email?: string;
  guest_phone: string;
  treatment_id: string;
  treatment?: SpaTreatment;
  room_id?: string;
  room?: SpaRoom;
  therapist_id?: string;
  therapist?: Therapist;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: SpaAppointmentStatus;
  notes?: string;
  price: number;
  paid_amount: number;
  payment_status: 'pending' | 'partial' | 'fully_paid' | 'failed';
  transaction_id?: string;
  payment_method?: string;
  created_at: string;
  updated_at: string;
}

export interface AppointmentPaymentInput {
  amount: number;
  method: string;
  transaction_id?: string;
  phone?: string;
  tip_amount?: number;
}

export interface PaginatedTreatments {
  treatments: SpaTreatment[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface SpaPaginatedRooms {
  rooms: SpaRoom[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedTherapists {
  therapists: Therapist[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedAppointments {
  appointments: SpaAppointment[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface TreatmentListParams {
  page?: number;
  page_size?: number;
  category?: string;
  status?: SpaTreatmentStatus;
}

export interface SpaRoomListParams {
  page?: number;
  page_size?: number;
  room_type?: string;
  status?: SpaRoomStatus;
}

export interface TherapistListParams {
  page?: number;
  page_size?: number;
  specialty?: string;
  is_active?: boolean;
}

export interface AppointmentListParams {
  page?: number;
  page_size?: number;
  status?: SpaAppointmentStatus;
  appointment_date?: string;
  therapist_id?: string;
}

export interface CreateTreatmentInput {
  name: string;
  code: string;
  category: string;
  duration_minutes: number;
  price: number;
  description?: string;
  image_url?: string;
  status?: SpaTreatmentStatus;
  is_featured?: boolean;
  sort_order?: number;
  amenities?: string[];
  contraindications?: string[];
}

export interface CreateRoomInput {
  name: string;
  code: string;
  room_type: string;
  capacity: number;
  area_sqm?: number;
  floor_level?: number;
  amenities?: string[];
  image_url?: string;
  is_active?: boolean;
}

export interface CreateTherapistInput {
  first_name: string;
  last_name: string;
  phone: string;
  email?: string;
  specialties: TherapistSpecialty[];
  certification?: string;
  years_experience?: number;
  bio?: string;
  image_url?: string;
  is_active?: boolean;
  working_days?: string[];
  working_hours_start?: string;
  working_hours_end?: string;
}

export interface CreateAppointmentInput {
  guest_id?: string;
  booking_id?: string;
  guest_name: string;
  guest_email?: string;
  guest_phone: string;
  treatment_id: string;
  room_id?: string;
  therapist_id?: string;
  appointment_date: string;
  start_time: string;
  notes?: string;
  price?: number;
}

function sanitizeAppointmentInput<T extends CreateAppointmentInput>(
  data: T,
): CreateAppointmentInput {
  const payload: CreateAppointmentInput = {
    guest_name: data.guest_name,
    guest_phone: data.guest_phone,
    treatment_id: data.treatment_id,
    appointment_date: data.appointment_date,
    start_time: data.start_time,
  };
  if (data.guest_id) payload.guest_id = data.guest_id;
  if (data.booking_id) payload.booking_id = data.booking_id;
  if (data.guest_email?.trim()) payload.guest_email = data.guest_email.trim();
  if (data.room_id) payload.room_id = data.room_id;
  if (data.therapist_id) payload.therapist_id = data.therapist_id;
  if (data.notes?.trim()) payload.notes = data.notes.trim();
  if (data.price && data.price > 0) payload.price = data.price;
  return payload;
}

export const treatmentService = {
  list: async (params?: TreatmentListParams): Promise<PaginatedTreatments> => {
    const res = await spaClient.get<PaginatedTreatments>('/api/v1/treatments', { params });
    return res.data;
  },
  getAll: async (): Promise<SpaTreatment[]> => {
    const res = await spaClient.get<SpaTreatment[]>('/api/v1/treatments/all');
    return res.data;
  },
  getFeatured: async (): Promise<SpaTreatment[]> => {
    const res = await spaClient.get<SpaTreatment[]>('/api/v1/treatments/featured');
    return res.data;
  },
  getByCategory: async (category: string): Promise<SpaTreatment[]> => {
    const res = await spaClient.get<SpaTreatment[]>(`/api/v1/treatments/category/${category}`);
    return res.data;
  },
  get: (id: string) => spaClient.get<SpaTreatment>(`/api/v1/treatments/${id}`),
  create: (data: CreateTreatmentInput) => spaClient.post<SpaTreatment>('/api/v1/treatments', data),
  update: (id: string, data: Partial<SpaTreatment>) =>
    spaClient.put<SpaTreatment>(`/api/v1/treatments/${id}`, data),
  delete: (id: string) => spaClient.delete(`/api/v1/treatments/${id}`),
};

export const spaRoomService = {
  list: async (params?: SpaRoomListParams): Promise<SpaPaginatedRooms> => {
    const res = await spaClient.get<SpaPaginatedRooms>('/api/v1/spa-rooms', { params });
    return res.data;
  },
  getAll: async (): Promise<SpaRoom[]> => {
    const res = await spaClient.get<SpaRoom[]>('/api/v1/spa-rooms/all');
    return res.data;
  },
  getAvailable: async (date?: string, startTime?: string, endTime?: string): Promise<SpaRoom[]> => {
    const res = await spaClient.get<SpaRoom[]>('/api/v1/spa-rooms/available', {
      params: { date, start_time: startTime, end_time: endTime },
    });
    return res.data;
  },
  get: (id: string) => spaClient.get<SpaRoom>(`/api/v1/spa-rooms/${id}`),
  create: (data: CreateRoomInput) => spaClient.post<SpaRoom>('/api/v1/spa-rooms', data),
  update: (id: string, data: Partial<SpaRoom>) =>
    spaClient.put<SpaRoom>(`/api/v1/spa-rooms/${id}`, data),
  delete: (id: string) => spaClient.delete(`/api/v1/spa-rooms/${id}`),
  updateStatus: (id: string, status: SpaRoomStatus) =>
    spaClient.patch<SpaRoom>(`/api/v1/spa-rooms/${id}/status`, { status }),
};

export const therapistService = {
  list: async (params?: TherapistListParams): Promise<PaginatedTherapists> => {
    const res = await spaClient.get<PaginatedTherapists>('/api/v1/therapists', { params });
    return res.data;
  },
  getAll: async (): Promise<Therapist[]> => {
    const res = await spaClient.get<Therapist[]>('/api/v1/therapists/all');
    return res.data;
  },
  getBySpecialty: async (specialty: string): Promise<Therapist[]> => {
    const res = await spaClient.get<Therapist[]>(`/api/v1/therapists/specialty/${specialty}`);
    return res.data;
  },
  get: (id: string) => spaClient.get<Therapist>(`/api/v1/therapists/${id}`),
  create: (data: CreateTherapistInput) => spaClient.post<Therapist>('/api/v1/therapists', data),
  update: (id: string, data: Partial<Therapist>) =>
    spaClient.put<Therapist>(`/api/v1/therapists/${id}`, data),
  delete: (id: string) => spaClient.delete(`/api/v1/therapists/${id}`),
};

export const appointmentService = {
  list: async (params?: AppointmentListParams): Promise<PaginatedAppointments> => {
    const res = await spaClient.get<PaginatedAppointments>('/api/v1/appointments', { params });
    return res.data;
  },
  getByDate: async (date: string): Promise<SpaAppointment[]> => {
    const res = await spaClient.get<SpaAppointment[]>(`/api/v1/appointments/date/${date}`);
    return res.data;
  },
  getToday: async (): Promise<SpaAppointment[]> => {
    const res = await spaClient.get<SpaAppointment[]>('/api/v1/appointments/today');
    return res.data;
  },
  get: (id: string) => spaClient.get<SpaAppointment>(`/api/v1/appointments/${id}`),
  getByReference: (ref: string) =>
    spaClient.get<SpaAppointment>(`/api/v1/appointments/reference/${ref}`),
  create: (data: CreateAppointmentInput) =>
    spaClient.post<SpaAppointment>('/api/v1/appointments', sanitizeAppointmentInput(data)),
  update: (id: string, data: Partial<SpaAppointment>) => {
    const payload: Partial<SpaAppointment> = { ...data };
    if (!payload.room_id) delete payload.room_id;
    if (!payload.therapist_id) delete payload.therapist_id;
    if (!payload.guest_email) delete payload.guest_email;
    return spaClient.put<SpaAppointment>(`/api/v1/appointments/${id}`, payload);
  },
  delete: (id: string) => spaClient.delete(`/api/v1/appointments/${id}`),
  confirm: (id: string) => spaClient.post<SpaAppointment>(`/api/v1/appointments/${id}/confirm`),
  cancel: (id: string, reason?: string) =>
    spaClient.post<SpaAppointment>(`/api/v1/appointments/${id}/cancel`, { reason }),
  start: (id: string) => spaClient.post<SpaAppointment>(`/api/v1/appointments/${id}/start`),
  complete: (id: string) => spaClient.post<SpaAppointment>(`/api/v1/appointments/${id}/complete`),
  markNoShow: (id: string) => spaClient.post<SpaAppointment>(`/api/v1/appointments/${id}/no-show`),
  pay: (id: string, data: AppointmentPaymentInput) =>
    spaClient.post<SpaAppointment>(`/api/v1/appointments/${id}/pay`, {
      amount: data.amount,
      method: data.method,
      transaction_id: data.transaction_id || '',
      phone: data.phone || '',
      tip_amount: data.tip_amount || 0,
    }),
  refund: (id: string, data: { amount?: number; reason?: string }) =>
    spaClient.post<SpaAppointment>(`/api/v1/appointments/${id}/refund`, data),
};

export const spaApi = {
  treatmentService,
  spaRoomService,
  therapistService,
  appointmentService,
};

export default spaApi;
